import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { isNonEmptyString, isNumber } from '@cayuse/shared-react.utils.type-checks';

import { useDictionary } from "hooks";

const CodeValidationContext = createContext({
	onBlur: index => index,
	totalPercentageError: "",
	customValidationErrors: []
});

export const useCodeValidationContext = () => useContext(CodeValidationContext);

const getTotalPercentageError =
	({
		includePercentage,
		isRequired,
		haveAllPercentsBlurred,
		totalPercentage,
		dictionary,
		hasOnePercentBlurred,
	}) => {
		let totalPercentageError = "";
		if (includePercentage) {
			if (isRequired) {
				if (haveAllPercentsBlurred && totalPercentage !== 100) {
					totalPercentageError = dictionary.totalPercentageRequired;
				}
			} else {
				if (hasOnePercentBlurred && totalPercentage !== 100 && totalPercentage !== 0) {
					totalPercentageError = dictionary.totalPercentageNotRequired;
				}
			}
		}

		return totalPercentageError;
	};

export const getCustomValidationErrors = ({ customValidations = [], hasBlurred = {} }) => {
	return customValidations
		.filter(validation => !validation.expression)
		.map(validation => validation.errorMsg);
};

export const CodeValidationContextWrapper = ({ includePercentage, isRequired, codes, children, customValidations = [] }) => {
	const dictionary = useDictionary("classificationCodes.errorMessages");
	const [ hasBlurred, setHasBlurred ] = useState({});
	const prevCodesLength = useRef(codes?.length || 0);
	const onBlur = useCallback(index =>
		setHasBlurred(hasBeenTouched => ({
			...hasBeenTouched,
			[index]: true,
		})),
	[],
	);
	const validationState = useMemo(() => {
		const codesValidState = {};
		let totalPercentage = 0;
		let hasOnePercentBlurred = false;
		let haveAllPercentsBlurred = true;
		for (let i = 0; i < codes.length; i++) {
			const { percentage } = codes[i];
			const hasPercentBlurred = hasBlurred[i];
			hasOnePercentBlurred = hasOnePercentBlurred || hasPercentBlurred;
			haveAllPercentsBlurred = haveAllPercentsBlurred && hasPercentBlurred;
			totalPercentage += +percentage;

			codesValidState[i] = !isRequired || !hasPercentBlurred || isNonEmptyString(`${percentage}`);
		}
		const totalPercentageError = getTotalPercentageError({
			includePercentage,
			isRequired,
			haveAllPercentsBlurred,
			totalPercentage,
			dictionary,
			hasOnePercentBlurred,
		});
		const customValidationErrors = getCustomValidationErrors({
			customValidations,
			hasBlurred
		});

		return {
			...codesValidState,
			totalPercentageError,
			customValidationErrors
		};
	}, [ codes, dictionary, hasBlurred, includePercentage, isRequired, customValidations ]);

	useEffect(() => {
		if (prevCodesLength.current !== codes.length) {
			prevCodesLength.current = codes.length;
			setHasBlurred(hasBeenTouched =>
				Object.keys(hasBeenTouched)
					.filter(index => index < codes.length)
					.reduce(
						(newHasBeenTouched, index) => ({ ...newHasBeenTouched, [index]: hasBeenTouched[index] }),
						{},
					));
		}
	},
	[ codes.length ],
	);

	return (
		<CodeValidationContext.Provider value={{ ...validationState, onBlur }}>
			{children}
		</CodeValidationContext.Provider>
	);
};
